import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Row, Col, Nav, Card } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { usePermissions } from "../../hooks/usePermissions";
import AccessosWiki from "./sections/AccessosWiki";
import AfiliadosWiki from "./sections/AfiliadosWiki";
import GerentesAfiliadosWiki from "./sections/GerentesAfiliadosWiki";
import ComissoesWiki from "./sections/ComissoesWiki";
import NotificacoesWiki from "./sections/NotificacoesWiki";
import PagsWiki from "./sections/PagsWiki";
import WelcomeWiki from "./sections/WelcomeWiki";
import SaqueWiki from "./sections/SaqueWiki";
import ProdutosQuePromovoWiki from "./sections/ProdutosQuePromovoWiki";
import LojaWiki from "./sections/LojaWiki";
import AfterPayWiki from "./sections/AfterPayWiki";
import TicketsWiki from "./sections/TicketsWiki";
import WebhooksWiki from "./sections/WebhooksWiki";
import BlingWiki from "./sections/BlingWiki";
import NotazzWiki from "./sections/NotazzWiki";
import OQueEApiWiki from "./sections/OQueEApiWiki";
import ApiVendasWiki from "./sections/ApiVendasWiki";
import ApiProdutosWiki from "./sections/ApiProdutosWiki";
import styles from './WikiPage.module.scss';
import './WikiGlobal.css';

/**
 * 📚 Pagmus Wiki - Central de Documentação
 * 
 * Página principal da Wiki com navegação lateral e conteúdo dinâmico.
 * Documentação voltada para produtores, não técnica.
 */
const WikiPage = () => {
  const { isOwner, isOwnerOrEmployee } = usePermissions();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('welcome');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Ler section da URL (?section=notazz)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    if (section) {
      setActiveSection(section);
    }
  }, [location.search]);

  // Seções disponíveis da Wiki
  const wikiSections = [
    {
      id: 'welcome',
      title: 'Início',
      icon: 'mdi:home',
      description: 'Bem-vindo à Pagmus Wiki',
      component: WelcomeWiki
    },
    {
      id: 'accessos',
      title: 'Acessos',
      icon: 'mdi:account-group',
      description: 'Grupos de permissões, usuários e controles de acesso',
      component: AccessosWiki
    },
    {
      id: 'afiliados',
      title: 'Afiliados e Vendedores',
      icon: 'mdi:account-network',
      description: 'Como criar, gerenciar e atribuir produtos a afiliados e vendedores',
      component: AfiliadosWiki
    },
    {
      id: 'gerentes-afiliados',
      title: 'Gerentes de Afiliados',
      icon: 'mdi:account-tie',
      description: 'Sistema de gerentes para recrutar e gerenciar equipes de afiliados',
      component: GerentesAfiliadosWiki
    },
    {
      id: 'comissoes',
      title: 'Comissões',
      icon: 'mdi:cash-multiple',
      description: 'Entenda como funciona o sistema de comissões e distribuição de valores',
      component: ComissoesWiki
    },
    {
      id: 'notificacoes',
      title: 'Notificações',
      icon: 'mdi:bell',
      description: 'Sistema de alertas em tempo real e como configurar suas preferências',
      component: NotificacoesWiki
    },
    {
      id: 'pags',
      title: 'Termômetro (PAGs)',
      icon: 'mdi:fire',
      description: 'Como funciona o termômetro de atratividade dos produtos na loja',
      component: PagsWiki
    },
    {
      id: 'saque',
      title: 'Saque',
      icon: 'mdi:cash-multiple',
      description: 'Como solicitar saques, acompanhar status e entender os saldos',
      component: SaqueWiki
    },
    {
      id: 'produtos-que-promovo',
      title: 'Produtos que Promovo',
      icon: 'mdi:share-variant',
      description: 'Gerencie produtos que você promove como afiliado e acompanhe comissões',
      component: ProdutosQuePromovoWiki
    },
    {
      id: 'loja',
      title: 'Loja',
      icon: 'mdi:store',
      description: 'Descubra produtos digitais e se torne afiliado para ganhar comissões',
      component: LojaWiki
    },
    {
      id: 'afterpay',
      title: 'Pedidos AfterPay',
      icon: 'mdi:package-variant-closed',
      description: 'Gerencie pedidos físicos com o sistema Kanban visual',
      component: AfterPayWiki
    },
    {
      id: 'tickets',
      title: 'Tickets e Suporte',
      icon: 'mdi:ticket-confirmation',
      description: 'Sistema de suporte completo para resolver dúvidas e problemas',
      component: TicketsWiki
    },
    {
      id: 'separator-integracao-api',
      type: 'separator',
      title: 'Integrações e API'
    },
    {
      id: 'webhooks',
      title: 'Webhooks',
      icon: 'mdi:webhook',
      description: 'Configure webhooks para receber notificações automáticas de eventos',
      component: WebhooksWiki
    },
    {
      id: 'bling',
      title: 'Integração Bling',
      icon: 'mdi:link-variant',
      description: 'Conecte sua conta Bling ERP e sincronize vendas automaticamente',
      component: BlingWiki
    },
    {
      id: 'notazz',
      title: 'Notas Fiscais (Notazz)',
      icon: 'mdi:file-document-check',
      description: 'Emita notas fiscais automaticamente para cada venda confirmada',
      component: NotazzWiki
    },
    {
      id: 'api-introducao',
      title: 'O que é a API',
      icon: 'mdi:api',
      description: 'Entenda o que é uma API e para que ela serve',
      component: OQueEApiWiki
    },
    {
      id: 'api-vendas',
      title: 'API de Vendas',
      icon: 'mdi:cart',
      description: 'Como usar a API para gerenciar vendas automaticamente',
      component: ApiVendasWiki
    },
    {
      id: 'api-produtos',
      title: 'API de Produtos',
      icon: 'mdi:package-variant',
      description: 'Como usar a API para gerenciar produtos automaticamente',
      component: ApiProdutosWiki
    },
  ];

  const currentSection = wikiSections.find(s => s.id === activeSection);
  const ActiveComponent = currentSection?.component || WelcomeWiki;

  // Wiki disponível para OWNER e Colaboradores (todos os blocos de permissão)
  if (!isOwner() && !isOwnerOrEmployee()) {
    return (
      <div className="container mt-4">
        <Card className="text-center p-5">
          <Icon icon="mdi:lock" size={64} className="text-muted mb-3" />
          <h4>Acesso Restrito</h4>
          <p className="text-muted">Acesso restrito apenas para usuários autenticados.</p>
        </Card>
      </div>
    );
  }

  const handleSectionClick = (sectionId) => {
    setActiveSection(sectionId);
    // Fechar drawer no mobile após selecionar seção
    setMobileMenuOpen(false);
  };

  return (
    <div className={styles.wikiContainer}>
      {/* Botão Mobile Menu - Apenas visível no mobile */}
      <button 
        className={styles.mobileMenuButton}
        onClick={() => setMobileMenuOpen(true)}
        aria-label="Abrir menu da Wiki"
      >
        <Icon icon="mdi:menu" size={24} />
        <span>Menu Wiki</span>
      </button>

      {/* Overlay para fechar drawer no mobile */}
      {mobileMenuOpen && (
        <div 
          className={styles.mobileOverlay}
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      <Row className="g-0">
        {/* Sidebar de Navegação */}
        <Col md={3} className={`${styles.sidebar} ${mobileMenuOpen ? styles.sidebarOpen : ''}`}>
          <div className={styles.sidebarHeader}>
            <Icon icon="mdi:book-open-page-variant" size={28} />
            <h4 className="mb-0 ms-2">Pagmus Wiki</h4>
            {/* Botão fechar no mobile */}
            <button 
              className={styles.closeButton}
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Fechar menu"
            >
              <Icon icon="mdi:close" size={24} />
            </button>
          </div>
          
          <Nav className="flex-column mt-4">
            {wikiSections.map((section) => {
              // Se for um separador, renderizar título
              if (section.type === 'separator') {
                return (
                  <div key={section.id} className={styles.separatorTitle}>
                    <span>{section.title}</span>
                  </div>
                );
              }
              
              // Se for uma seção normal, renderizar link
              return (
                <Nav.Link
                  key={section.id}
                  className={`${styles.navLink} ${activeSection === section.id ? styles.active : ''}`}
                  onClick={() => handleSectionClick(section.id)}
                >
                  <Icon icon={section.icon} size={20} />
                  <span className="ms-2">{section.title}</span>
                </Nav.Link>
              );
            })}
          </Nav>

          <div className={styles.sidebarFooter}>
            <small className="text-muted">
              <Icon icon="mdi:information" size={16} className="me-1" />
              Documentação atualizada
            </small>
          </div>
        </Col>

        {/* Conteúdo Principal */}
        <Col xs={12} md={9} className={styles.content}>
          <div className={styles.contentHeader}>
            <div>
              <span className={styles.pageTitle}>
                <Icon icon={currentSection?.icon} className="me-2" />
                {currentSection?.title}
              </span>
              <p className="text-muted mb-0">{currentSection?.description}</p>
            </div>
          </div>

          <div className={styles.contentBody}>
            <ActiveComponent />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default WikiPage;

